import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import BorderIcon from '@atlaskit/icon/core/border';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import MinimizeIcon from '@atlaskit/icon/core/minimize';
import { Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ColorPicker, useBuiltInColorMap } from '@atlassian/color-picker';
import { CollapsibleItem, useProductDependencies } from '@atlassian/company-hub-common';
import { CornerStyleConfig } from '@atlassian/company-hub-common/config-panel';
import {
	resolveAdfColorForPicker,
	toAdfColor,
} from '@atlassian/company-hub-common/hub-brand-palette';
import {
	HUB_CORNER_RADIUS_MAX,
	HUB_CORNER_RADIUS_MIN,
	HUB_CORNER_RADIUS_STEP,
	resolveCornerRadiusOverride,
	resolveHubCornerRadius,
} from '@atlassian/company-hub-common/hub-corner-radius';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';
import { RangeField } from '@atlassian/company-hub-common/range-field';
import { CornerStyle } from '@atlassian/company-hub-common/types';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';

import type { LinksMenuExtensionActions } from '../../../../controllers/use-links-menu-parameters-and-actions';
import { BORDER, CORNERS, type LinksMenuParameters } from '../../../../types';

import { messages } from './messages';

const styles = cssMap({
	borderOptionContainer: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	dropdownTrigger: {
		backgroundColor: token('color.background.neutral.subtle'),
	},
	border: {
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
	},
	selected: {
		backgroundColor: token('color.background.selected'),
		borderColor: token('color.border.accent.blue'),
	},
});

export type BackgroundConfigProps = Pick<
	LinksMenuParameters['styles'],
	'border' | 'borderColor' | 'cornerRadius' | 'cornerStyle' | 'fillColor'
> &
	Pick<
		LinksMenuExtensionActions,
		| 'updateBorder'
		| 'updateBorderColor'
		| 'updateCornerRadius'
		| 'updateCornerStyle'
		| 'updateFillColor'
	> & {
		isReadableFillColor: (color: string) => boolean;
	};

// TODO: COMPHUB-3164 - replace this background color field with the reusable one once it's implemented. Refer to ticket for more context
export const BackgroundConfig: React.FC<BackgroundConfigProps> = ({
	border,
	updateBorder,
	borderColor,
	updateBorderColor,
	fillColor,
	updateFillColor,
	cornerStyle,
	updateCornerStyle,
	cornerRadius,
	updateCornerRadius,
	isReadableFillColor,
}) => {
	const { formatMessage } = useIntl();
	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	const { isSSRProductInDarkMode } = useProductDependencies();
	const { branding } = useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};

	const { backgroundColorMap, borderColorMap, emptyColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);

	// Advanced-theming opt-in: when the framework is mounted with gate ON,
	// swap the swatch palette for the 22-shade brand map and translate the
	// picker hex <-> ADF storage shape via the palette helpers. Gate OFF (or
	// when the provider isn't mounted, e.g. Confluence Company Hub) falls
	// back to today's behavior unchanged.
	const theming = useHubTheming();
	const isAdvancedThemingEnabled = theming?.isAdvancedThemingEnabled === true;
	const palette = isAdvancedThemingEnabled ? theming?.palette : undefined;
	const primaryColor = isAdvancedThemingEnabled ? theming?.primaryColor : undefined;
	const v2BgMap = isAdvancedThemingEnabled ? theming?.brandBackgroundColorMap : undefined;
	const v2BorderMap = isAdvancedThemingEnabled ? theming?.brandBorderColorMap : undefined;

	// Bg + border are always populated by the manifest's defaultParameters
	// (or DEFAULT_STYLES), so the fallback ref isn't hit in practice — it's
	// only used by resolveAdfColorForPicker if the stored value is null.
	// Pass primary.200 as a sensible visible default.
	const fillPickerValue = palette
		? resolveAdfColorForPicker(fillColor, palette, 'primary.200', primaryColor)
		: fillColor;
	const borderPickerValue = palette
		? resolveAdfColorForPicker(borderColor, palette, 'primary.200', primaryColor)
		: borderColor;

	const handleFillColorChange = (color: string) => {
		// Note: text auto-flip on bg change is handled centrally by the
		// `useAutoTransitioningColors` hook in the StylesTab parent, so we
		// only need to persist the bg here. When v2 is ON, store as the
		// palette var-ref form so the bg tracks future brand-color changes.
		updateFillColor(palette ? toAdfColor(color, palette) : color);
	};

	const handleBorderColorChange = (color: string) => {
		if (border === BORDER.NONE) {
			// if user interacts with border color then they need a border
			// position in order to see their changes
			updateBorder(BORDER.ALL_SIDES);
		}
		// When v2 is ON, store brand-palette swatches as palette var-refs so
		// the border tracks future brand-color changes. The 4 neutral
		// swatches (white, color.border hex, color.border.bold hex, black)
		// and any custom hex fall through `toAdfColor` and get stored
		// verbatim as raw hex.
		updateBorderColor(palette ? toAdfColor(color, palette) : color);
	};

	const handleBorderOptionClick = (borderOption: BORDER) => updateBorder(borderOption);

	return (
		<CollapsibleItem title={formatMessage(messages.backgroundTitle)}>
			<Stack space="space.200">
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text">
						<FormattedMessage {...messages.backgroundColor} />
					</Text>
					<ColorPicker
						analyticsSource="linksMenuExtensionConfigBackgroundColor"
						builtInColorMap={
							v2BgMap ??
							(hideDefaultColorPalette && brandBackgroundColorMap
								? brandBackgroundColorMap
								: backgroundColorMap)
						}
						isReadableColor={isReadableFillColor}
						onChange={handleFillColorChange}
						value={fillPickerValue}
						pickerTitle={messages.backgroundColor}
						showCustomPalette
					/>
				</Inline>
				<Inline alignBlock="center" spread="space-between">
					<Text>
						<FormattedMessage {...messages.borderPosition} />
					</Text>
					<Inline space="space.100">
						{[
							{
								borderOption: BORDER.NONE,
								label: messages.noBorderLabel,
								IconComponent: EyeOpenStrikethroughIcon,
							},
							{
								borderOption: BORDER.ALL_SIDES,
								label: messages.allBordersLabel,
								IconComponent: BorderIcon,
							},
							{
								borderOption: BORDER.BOTTOM,
								label: messages.bottomBorderLabel,
								IconComponent: MinimizeIcon,
							},
						].map(({ borderOption, label, IconComponent }) => {
							return (
								<Tooltip key={borderOption} content={formatMessage(label)}>
									<Pressable
										xcss={cx(
											styles.borderOptionContainer,
											styles.border,
											border === borderOption && styles.selected,
										)}
										onClick={() => handleBorderOptionClick(borderOption)}
										testId={`border-${borderOption}${border === borderOption ? '--selected' : ''}`}
									>
										<IconComponent
											color={
												border === borderOption
													? token('color.icon.accent.blue')
													: token('color.icon')
											}
											label={formatMessage(label)}
										/>
									</Pressable>
								</Tooltip>
							);
						})}
					</Inline>
				</Inline>
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text">
						<FormattedMessage {...messages.borderColor} />
					</Text>
					<ColorPicker
						analyticsSource="linksMenuExtensionConfigBorderColor"
						builtInColorMap={
							v2BorderMap ?? (hideDefaultColorPalette ? emptyColorMap : borderColorMap)
						}
						onChange={(color) => color && handleBorderColorChange(color)}
						value={borderPickerValue}
						pickerTitle={messages.borderColor}
						showCustomPalette
					/>
				</Inline>
				{isCornerRadiusEnabled ? (
					<RangeField
						label={messages.cornerRadius}
						minValue={HUB_CORNER_RADIUS_MIN}
						maxValue={HUB_CORNER_RADIUS_MAX}
						step={HUB_CORNER_RADIUS_STEP}
						value={resolveHubCornerRadius(
							resolveCornerRadiusOverride(
								cornerRadius,
								cornerStyle === CORNERS.SHARP ? CornerStyle.SQUARED : CornerStyle.ROUNDED,
							) ?? theming?.cornerRadius,
						)}
						updateRangeValue={updateCornerRadius}
						analyticSource="linksMenuExtensionConfigCornerRadius"
						prefix="cornerRadius"
						unit="px"
					/>
				) : (
					<CornerStyleConfig<BackgroundConfigProps['cornerStyle']>
						cornerStyleValue={cornerStyle}
						updateCornerStyle={updateCornerStyle}
						roundCornerValue={CORNERS.ROUND}
						squareCornerValue={CORNERS.SHARP}
					/>
				)}
			</Stack>
		</CollapsibleItem>
	);
};
